import {ButtonProps} from '@mui/material/Button';
import type {Theme} from '@mui/material/styles';
import type {SystemStyleObject} from '@mui/system';

import {
  actions,
  greenColors,
  orangeColors,
  neutral,
  warningColor,
  warningColorLight,
  warningColorDark,
} from '../../../layouts/theme/designSystem';

export type EmpionButtonVariant = ButtonProps['variant'] | 'primary' | 'secondary' | 'menu' | 'cta' | 'warning';

type GetEmpionButtonStyleParams = {
  resolvedVariant: ButtonProps['variant'];
  variant?: EmpionButtonVariant;
  color: ButtonProps['color'];
  disabledColor?: string;
  textColor?: string;
  hoverTextColor?: string;
  hoverBackgroundColor?: string;
};

const getHoverBackgroundColor = (color: string) => {
  return color === 'primary' ? 'primary.light' : 'secondary.light';
};

const getContainedVariantSx = ({
  variant,
  color,
  disabledColor,
  textColor,
  hoverTextColor,
  hoverBackgroundColor,
}: GetEmpionButtonStyleParams): SystemStyleObject<Theme> => {
  const isCtaVariant = variant === 'cta';
  const isWarningVariant = variant === 'warning';
  const baseTextColor = textColor || 'white';
  const hoverColor = hoverTextColor || 'white';
  const containedBackground = isCtaVariant ? actions.cta : isWarningVariant ? warningColor : actions.primary;
  const containedHoverBackground = isCtaVariant
    ? hoverBackgroundColor || actions.ctaHover
    : isWarningVariant
      ? hoverBackgroundColor || warningColorDark
      : hoverBackgroundColor || actions.primaryHover || getHoverBackgroundColor(color || 'secondary');
  const containedDisabledBackground =
    disabledColor || (isWarningVariant ? warningColorLight : actions.ctaDisabled);

  return {
    '&&.MuiButton-contained': {
      color: baseTextColor,
      backgroundColor: containedBackground,
    },
    '&&.MuiButton-contained:hover': {
      backgroundColor: containedHoverBackground,
      boxShadow: 'none',
      color: hoverColor,
    },
    '&&.MuiButton-contained.Mui-disabled': {
      backgroundColor: containedDisabledBackground,
      color: textColor || 'white',
      opacity: 0.6,
    },
  };
};

const getMenuVariantSx = (): SystemStyleObject<Theme> => ({
  fontSize: 16,
  fontWeight: 500,
  lineHeight: 1.45,
  minHeight: 32,
  px: 1.75,
  py: 0.875,
});

const getOutlinedVariantSx = ({textColor, hoverTextColor}: GetEmpionButtonStyleParams): SystemStyleObject<Theme> => {
  const baseTextColor = textColor || greenColors[700];
  const hoverColor = hoverTextColor || greenColors[700];

  return {
    '&&.MuiButton-outlined': {
      borderWidth: 1,
      color: baseTextColor,
      borderColor: orangeColors[3],
    },
    '&&.MuiButton-outlined:hover': {
      backgroundColor: orangeColors[5],
      borderColor: orangeColors[40],
      borderWidth: 1,
      boxShadow: 'none',
      color: hoverColor,
    },
    '&&.MuiButton-outlined.Mui-disabled': {
      backgroundColor: 'transparent',
      borderColor: orangeColors[3],
      borderWidth: 1,
      color: textColor || neutral[300],
      opacity: 0.6,
    },
  };
};

export const getEmpionButtonStyle = (options: GetEmpionButtonStyleParams): SystemStyleObject<Theme> => ({
  ...(options.variant === 'menu'
    ? getMenuVariantSx()
    : {
      fontWeight: 700,
      lineHeight: '30px',
    }),
  borderRadius: '8px',
  textTransform: 'none',
  boxShadow: 'none',
  ...(options.resolvedVariant === 'contained' ? getContainedVariantSx(options) : {}),
  ...(options.resolvedVariant === 'outlined' ? getOutlinedVariantSx(options) : {}),
});
